import type { Dict } from "./types";

/** Româna.
 *
 *  Două lucruri respectate peste tot: numele de tehnologii nu se traduc
 *  („PostgreSQL" rămâne „PostgreSQL", „runbook" rămâne „runbook", fiindcă un
 *  inginer român le caută așa), iar tonul rămâne cel din engleză — direct,
 *  concret, fără limbaj de agenție. „Soluții complexe pentru business" e exact
 *  ce nu scrie nicăieri pe site.
 *
 *  „Forjat" în titlu e deliberat: marca se numește coresmith, iar fierarul e
 *  metafora, deci se păstrează și în traducere. */
export const ro: Dict = {
  tag: "ro",

  nav: { index: "Acasă", services: "Servicii", work: "Lucrări", blog: "Blog", faq: "Răspunsuri", contact: "Contact", menu: "Meniu", closeMenu: "Închide meniul" },

  workPage: {
    eyebrow: "Lucrări",
    title: "Șapte proiecte, șase dintre ele în producție",
    intro: "Fiecare proiect de aici a fost livrat sau rulează astăzi, iar fiecare are pagina lui cu problema, abordarea și ce a schimbat de fapt. Unul e un experiment deschis și scrie asta.",
    open: "Deschide cazul",
    task: "Problema",
    solution: "Soluția",
    result: "Rezultatul",
  },

  servicesPage: { eyebrow: "Servicii", title: "Șapte lucruri pe care le construim, și ce intră în fiecare", intro: "Fără pachete și fără niveluri. Fiecare dintre ele e o muncă reală, cu riscurile ei, iar lista de dedesubt e ce intră de fapt în ea — nu un meniu scris ca să pară complet." },

  faqPage: { eyebrow: "Răspunsuri", title: "Cât costă un proiect, cât durează, și ce rămâne al tău la final", intro: "Întrebările care decid dacă ne scrii, cu răspuns și la părțile pe care o ofertă le ascunde de obicei. Dacă a ta nu e aici, pune-o în formular — răspundem oricum." },

  contactPage: {
    eyebrow: "Contact",
    title: "Spune-ne ce trebuie schimbat și primești răspuns în 24 de ore",
    intro: "Cel mai rapid drum e formularul de mai jos: două clicuri și ce vrei să ne spui despre proiect. Dacă preferi să scrii pur și simplu, adresa e dedesubt.",
    directHead: "Sau scrie direct",
    directText: "Un email simplu funcționează la fel de bine ca formularul. Nu pierzi nimic dacă îl sari.",
    callHead: "Sau programează apelul",
    callText: "Dacă preferi să vorbim întâi, ia-ți direct un interval. Nu-ți trebuie formularul înainte — stabilim perimetrul în apel.",
    callBtn: "Programează un apel",
    chatHead: "Sau scrie-ne pe",
    chatText: "WhatsApp sau Telegram, care îți e deja deschis. Același răspuns, doar că pornește mai repede: ajung la un om, nu la un bot și nu într-o cutie comună.",
    whereHead: "Unde suntem",
    hours: "Luni–vineri, 9:00–18:00",
    nextHead: "Ce se întâmplă după ce trimiți",
    next: [
      "Îl citește un om — fără coadă și fără răspuns automat.",
      "Primești răspuns în 24 de ore, inclusiv când nu e ceva pentru noi.",
      "Dacă e, pasul următor e un apel gratuit, nu o factură.",
    ],
  },

  seeAll: { services: "Toate cele șapte servicii", faq: "Toate răspunsurile", work: "Toate lucrările" },

  home: {
    h1: ["Software forjat", "pentru producție."],
    sub: "Site-uri, aplicații desktop, automatizări. Livrate cap-coadă, apoi ținute în funcțiune.",
    ctaStart: "Începe un proiect",
    ctaWork: "Vezi lucrările",
    scale: [
      { n: "4", l: "ani" },
      { n: "20", l: "proiecte" },
      { n: "6", l: "țări" },
    ],
    painsHead: "Unde te doare?",
    workHead: "Lucrări selectate",
    servicesHead: "Servicii",
    stepsHead: "Cum lucrăm",
    stepsEyebrow: "De la primul apel până la rulat în producție",
    faqHead: "Întrebări frecvente",
    faqEyebrow: "Tot ce întreabă cineva înainte de primul apel",
    briefHead: "Începe un proiect",
    rows: [
      {
        nm: "ERP de retail cu mai multe depozite",
        st: "Trei locații pe un singur stoc. De la vânzare la bon fiscal, dintr-un clic. · Desktop, 2025",
      },
      {
        nm: "Computer vision cu AI pe dispozitiv",
        st: "30 fps pe dispozitiv, fără drum până în cloud. · Embedded, 2026",
      },
      {
        nm: "Magazin online bilingv",
        st: "1 500 de vizitatori pe zi, două limbi, plăți cu cardul funcționale. · Web, 2025",
      },
      {
        nm: "Bot de research pentru trading, cu AI",
        st: "~+1,5% P&L median zilnic — pe hârtie, încă un experiment. · Automatizare, 2026",
      },
    ],
    services: [
      {
        id: "cap-web",
        title: ["Web și", "e-commerce"],
        p: "Magazine, portaluri, dashboarduri și platforme de rezervări — inclusiv instalația de plăți și fiscalitate din spatele lor.",
        items: ["Magazine online și cataloage", "Portaluri și dashboarduri", "Plăți cu cardul și bonuri fiscale", "Multilingv, indexabil pe fiecare limbă"],
        kind: "Web app",
      },
      {
        title: ["Aplicații", "mobile"],
        p: "iOS și Android nativ acolo unde contează hardware-ul, cross-platform acolo unde contează acoperirea — și un răspuns onest despre care dintre ele îți trebuie.",
        items: ["iOS și Android nativ", "Cross-platform, un singur cod", "Date offline-first", "Notificări push și deep links"],
        kind: "Mobile app",
      },
      {
        id: "cap-desktop",
        title: ["Desktop și", "embedded"],
        p: "Software pentru magazin și hală: merge când rețeaua nu merge, vorbește cu aparatele și rulează pe hardware pe care îl putem desena dacă încă nu există.",
        items: ["Aplicații desktop pe orice sistem", "Offline din start, se sincronizează la revenire", "Aparate pe serial, USB și fiscale", "PCB propriu când nicio placă nu se potrivește"],
        kind: "Desktop",
      },
      {
        id: "cap-auto",
        title: ["Automatizări și", "integrări"],
        p: "Sistemele pe care deja le plătești, făcute să vorbească între ele, ca nimeni să nu mai retasteze nimic pe al doilea ecran.",
        items: ["Integrare ERP, CRM și baze de date", "API-uri străine și webhooks", "Notificări pe Telegram și email", "Sarcini programate și fluxuri de date"],
        kind: "Automation",
      },
      {
        title: ["AI care", "ajunge în producție"],
        p: "Modele folosite acolo unde își merită locul, cu cod determinist obișnuit care le ține granițele.",
        items: ["Detecție și OCR pe dispozitiv", "Funcții cu LLM, verificate de om", "Text generat, corectat înainte de publicare", "Limite deterministe în jurul modelului"],
        kind: "Automation",
      },
      {
        title: ["Date și", "dashboarduri"],
        p: "Cifrele care îți spun dacă lucrul funcționează, într-un singur loc, nu în cinci exporturi pe care nu le potrivește nimeni.",
        items: ["Model de date și proiectare de schemă", "Dashboarduri de raportare", "Analytics fără urmărire agresivă", "Mutarea datelor din Excel"],
        kind: "Automation",
      },
      {
        title: ["Audit", "tehnic"],
        p: "O citire a ceea ce ai deja, înainte să decizi dacă repari sau înlocuiești. Primești concluziile indiferent dacă ne angajezi sau nu.",
        items: ["Performanță și Core Web Vitals", "SEO tehnic și indexabilitate", "Arhitectură și citire de cod", "Verificare de dependențe și securitate"],
        kind: "Not sure yet",
      },
    ],
    steps: [
      {
        n: "01",
        h: "Primul apel",
        p: "Un apel gratuit în care ascultăm problema și spunem dacă suntem potriviți pentru ea. Fără prezentare, fără factură de „descoperire”. Dacă nu e pentru noi, afli aici, nu peste trei săptămâni.",
      },
      {
        n: "02",
        h: "Perimetru",
        p: "Fluxuri, model de date, cum se măsoară succesul. Stabilim ce înseamnă gata înainte să se construiască ceva, iar prețul e fix pe ce am stabilit — sau retainer, dacă lucrarea nu are capăt clar.",
      },
      {
        n: "03",
        h: "Design",
        p: "Tokenuri și componente o singură dată, refolosite peste tot. Fără șabloane și fără pachete: produsul tău nu iese arătând ca ultimul pe care l-am livrat.",
      },
      {
        n: "04",
        h: "Construcție",
        p: "Frontend, backend, stratul de date, integrări. Vezi progresul în fiecare săptămână și aprobi fiecare etapă înainte să înceapă următoarea, deci nimic nu apare ca surpriză la final.",
      },
      {
        n: "05",
        h: "Operare",
        p: "CI/CD, monitorizare, runbook. Nu dispărem la lansare — dar nici nu rămâi legat de noi: codul, documentația și runbookul sunt ale tale și pot fi rulate fără noi.",
      },
    ],
  },

  pains: {
    items: [
      "Am nevoie de un site care aduce clienți, nu complimente",
      "Echipa mea retastează aceleași date toată ziua",
      "Programele mele nu vorbesc între ele",
      "În magazin am nevoie de un program care merge și fără internet",
      "Am hardware și nimic care să ruleze pe el",
      "Știu ce mă doare, nu ce trebuie construit",
    ],
    outHead: "Nu e pe listă?",
    outText: "Descrie-o în cuvintele tale. Răspundem oricum, inclusiv când nu e ceva pentru noi.",
    outBtn: "Descrie problema",
    defects: [
      { n: "Porozitate", leg: "Sănătos pe dinafară, gol pe dinăuntru. Pare terminat și nu duce nimic." },
      { n: "Canal de uzură", leg: "Aceeași trecere, în fiecare zi, sapă un șanț pe care nu l-a bugetat nimeni." },
      { n: "Sudură rece", leg: "Două fluxuri s-au întâlnit și nu s-au sudat. Cusătura ține până la prima sarcină." },
      { n: "Zonă influențată termic", leg: "Lângă proces, metalul nu mai e metalul din catalog." },
      { n: "Cavitate neumplută", leg: "Cavitatea e prelucrată. N-a curs niciodată nimic în ea." },
      { n: "Cotă fără valoare", leg: "Măsurătoarea e pregătită. Numărul e chiar ce căutăm." },
    ],
  },

  brief: {
    steps: [
      { q: "Ce construim?", opts: ["Aplicație web", "Aplicație mobilă", "Desktop", "Automatizare", "Încă nu știu"] },
      { q: "Când începe?", opts: ["Cât mai repede", "1–3 luni", "Flexibil"] },
    ],
    detailsQ: "Povestește-mi despre el",
    detailsPlaceholder: "Ce trebuie să facă, ce există deja și cu ce trebuie să vorbească. Poate fi și pe scurt — restul întreb la apel.",
    detailsOptional: "Opțional, dar cu cât scrii mai mult, cu atât primul meu răspuns e mai util.",
    mailQ: "Unde trimit propunerea?",
    mailPlaceholder: "adresa@ta.com",
    send: "Trimite cererea",
    sending: "Se trimite…",
    sendByEmail: "Trimite pe email",
    botFailed:
      "Verificarea anti-bot nu poate rula în acest browser, deci formularul nu poate trimite. Butonul de sus deschide un email cu răspunsurile deja completate.",
    badEmail: "Adresa de email nu pare corectă.",
    generic: "Ceva n-a funcționat.",
    doneText: "Răspund în 24 de ore.",
    another: "Trimite încă unul",
    block: "Bloc de titlu",
  },

  serviceBack: "← Toate serviciile",
  stackNote: "Un link lângă un grup înseamnă că rulează în producție într-un proiect pe care îl poți citi. Restul e ce mai acoperă echipa.",
  stackProof: "În producție:",
  stackAlso: "Mai acoperim",
  serviceOther: "Celelalte servicii",
  serviceMore: "Citește despre acest serviciu",
  servicePages: [
    {
      slug: "web", num: "01", eyebrow: "Web și e-commerce", kind: "Web app",
      title: "Magazine, portaluri și instalația de plăți din spatele lor",
      intro:
        "Un proiect web e rareori doar niște pagini. E un catalog care trebuie să rămână corect, o plată care trebuie să treacă printr-o bancă anume, un bon care trebuie să mulțumească fiscul, și o a doua limbă care trebuie să fie completă, nu tradusă pe jumătate. Construim tot, nu partea care se fotografiază bine.",
      includedHead: "Ce intră",
      included: [
        { h: "Magazine și cataloage", p: "Produsele ca date, nu ca pagini. Un articol nou e un rând în bază, deci catalogul nu putrezește pe măsură ce crește, iar căutarea și filtrele merg la fel și la o mie de produse." },
        { h: "Portaluri și panouri", p: "Ecranele în care oamenii tăi stau toată ziua. Construite pentru viteza de introducere, nu pentru captură: tastatura întâi, fără fereastră care acoperă rândul pe care tocmai îl citeai." },
        { h: "Plăți cu cardul și bonuri fiscale", p: "Rețele locale de plată, nu doar Stripe. Am livrat plăți MAIB cu facturare fiscală la finalizarea comenzii — partea pe care majoritatea proiectelor o descoperă prea târziu." },
        { h: "Multilingv, indexabil pe fiecare limbă", p: "Fiecare limbă are adresa ei și meta ei, deci ambele versiuni pot rankui separat. Conținutul stă pe limbă la nivel de câmp, deci o pagină poate fi completă într-una și în lucru în alta fără să se rupă." },
        { h: "Prerandat pentru crawlere", p: "Fiecare rută e randată în HTML static la build, deci un motor de căutare sau un scraper de social primește pagina întreagă, nu o carcasă goală care se umple după JavaScript." },
      ],
      buildsHead: "Ce construim",
      builds: [
        { h: "Magazine online și cataloage", p: "Stoc, variante, comenzi și un checkout care trece prin banca pe care clienții tăi chiar o folosesc, cu bonul pe care fiscul îl așteaptă." },
        { h: "Platforme de rezervări", p: "Disponibilitate, reguli de preț, confirmări și calendarul dispecerului — cu notificarea care ajunge pe telefon, nu într-o cutie poștală pe care n-o deschide nimeni." },
        { h: "Portaluri de client și panouri interne", p: "Ecranele în care echipa lucrează toată ziua: comenzi, documente, statusuri, roluri și cine are voie să schimbe ce." },
        { h: "Marketplace-uri cu mai mulți vânzători", p: "Conturi de vânzător, comisioane, plăți și moderare — partea care transformă un catalog într-un loc unde pot vinde și alții." },
        { h: "Site-uri de firmă în mai multe limbi", p: "Fiecare limbă o versiune reală, cu adresa și meta ei, nu un plugin care traduce peste una singură." },
        { h: "CMS și publicare headless", p: "WordPress sau un CMS headless în spatele unui front-end rapid, ca marketingul să publice fără să aștepte un programator." },
      ],
      stackHead: "Stiva",
      stack: [
        { h: "Front-end", items: ["React", "TypeScript", "Vite", "Tailwind", "shadcn/ui", "i18next", "react-hook-form", "Sass"], proof: "createli", also: ["Next.js", "Vue", "Nuxt", "Astro"] },
        { h: "CMS și PHP", items: ["WordPress", "WooCommerce", "PHP", "Laravel"] },
        { h: "Back-end și date", items: ["Node.js", "Express", "PostgreSQL", "MySQL", "Sequelize", "API-uri REST"], proof: "commerce", also: ["Python", "FastAPI", "Django", ".NET", "Java", "Spring", "MongoDB", "Redis", "ClickHouse"] },
        { h: "Plăți și bonuri fiscale", items: ["MAIB", "facturare fiscală"], proof: "commerce", also: ["MICB", "PayNet", "Stripe", "PayPal"] },
        { h: "Găzduire și indexare", items: ["nginx", "PM2", "prerandare Puppeteer", "JSON-LD", "sitemap", "hreflang"], proof: "elegance", also: ["AWS", "Google Cloud", "Azure", "Docker", "Kubernetes", "Vercel", "Netlify", "Fly.io"] },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Magazine care vând în mai multe limbi", p: "Dacă nu toți clienții tăi citesc aceeași limbă, un plugin de traducere te costă fiecare piață pe care o atinge. Câte o versiune completă pentru fiecare limbă costă mai puțin decât una stricată întinsă peste toate." },
        { h: "Firme cu o problemă de plată locală", p: "Plăți prin bancă regională, bonuri fiscale, reguli de facturare pe care nicio platformă internațională nu le implementează. Aici e muncă obișnuită, în altă parte e exotică." },
        { h: "Oricine are un site invizibil pentru căutare", p: "Dacă paginile tale sunt o aplicație JavaScript, s-ar putea ca un crawler să nu primească absolut nimic. Se verifică într-un minut și se repară într-o zi." },
      ],
    },
    {
      slug: "mobile", num: "02", eyebrow: "Aplicații mobile", kind: "Mobile app",
      title: "Aplicații pentru telefonul clientului tău, și pentru al șoferului",
      intro:
        "O aplicație își merită costul atunci când telefonul face ceva ce o filă de browser nu poate: merge fără semnal, citește un cod de bare, ține sesiunea unui șofer toată ziua sau trimite o notificare care chiar ajunge. Construim nativ acolo unde contează hardware-ul și cross-platform acolo unde contează acoperirea — și îți spunem care e cazul tău înainte să plătești pentru vreunul.",
      includedHead: "Ce intră",
      included: [
        { h: "iOS și Android nativ", p: "Swift pe iOS, Kotlin pe Android. Drumul corect când aplicația se sprijină pe dispozitiv: cameră, locație în fundal, Bluetooth, stocare securizată, sau o viteză pe care un înveliș nu ți-o dă." },
        { h: "Cross-platform, un singur cod", p: "React Native sau Flutter când aplicația e mai ales ecrane și date. Ambele magazine dintr-un singur cod înjumătățesc costul fiecărei schimbări de după lansare." },
        { h: "Date offline-first", p: "Aplicația își ține datele local și le împacă la revenirea rețelei. Un curier într-un subsol și un depozit fără acoperire sunt cazul normal, nu excepția." },
        { h: "Notificări push și deep links", p: "O notificare care deschide ecranul corect, nu ecranul principal — inclusiv jumătatea nespectaculoasă: token-uri, livrări care eșuează în tăcere, și ce se întâmplă după o reinstalare." },
        { h: "Publicare în magazine și actualizări", p: "Reguli de review, declarații de confidențialitate, semnare și canalul de update de după. Publicat pe contul tău de dezvoltator, nu pe al nostru, ca aplicația să rămână a ta." },
      ],
      buildsHead: "Ce construim",
      builds: [
        { h: "Aplicații pentru un sistem pe care îl ai deja", p: "Jumătatea de telefon a unui magazin, a unui ERP sau a unei platforme de rezervări, care vorbește cu aceeași bază pe care o folosește deja aplicația web." },
        { h: "Aplicații de teren și de curierat", p: "Rute, scanări, semnături și fotografii luate offline și urcate când revine semnalul." },
        { h: "Aplicații de comandă și rezervare", p: "Catalog, coș, plată și status al comenzii, cu notificări care urmăresc comanda, nu o cutie poștală." },
        { h: "Fidelizare și conturi de client", p: "Carduri, puncte, istoric și oferte — motivul pentru care o aplicație e încă instalată la o lună după prima comandă." },
        { h: "Aplicații care vorbesc cu hardware", p: "Cântare Bluetooth, imprimante, scanere și terminale. Categoria în care nativul e o cerință, nu o preferință." },
        { h: "Aplicații web instalabile", p: "Când o listare în magazin adaugă cost fără să adauge acoperire: se instalează direct din browser, fără coadă de review și fără treizeci la sută." },
      ],
      stackHead: "Stiva",
      stack: [
        { h: "Nativ", items: ["Swift", "SwiftUI", "Kotlin", "Jetpack Compose", "Xcode", "Android Studio"] },
        { h: "Cross-platform", items: ["React Native", "Expo", "Flutter", "Dart", "TypeScript"] },
        { h: "Web instalabil", items: ["React", "service workers", "Web Push", "cache offline", "Web App Manifest"] },
        { h: "Back-end-ul cu care vorbește aplicația", items: ["Node.js", "Express", "PostgreSQL", "MySQL", "API-uri REST", "sesiuni pe token"], proof: "commerce", also: ["Python", "FastAPI", "Django", "Redis", "WebSocket"] },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Firme ai căror clienți sunt deja pe telefon", p: "Dacă mare parte din trafic e mobil și checkout-ul e locul unde oamenii renunță, o aplicație merită estimată înainte de încă un redesign." },
        { h: "Echipe care lucrează departe de birou", p: "Curieri, tehnicieni, șoferi, inspectori. Oameni cu telefonul într-o singură mână, care au nevoie de trei atingeri, nu de un formular." },
        { h: "Oricine a fost convins că-i trebuie ambele platforme", p: "Uneori chiar îi trebuie. Deseori o platformă plus o aplicație web instalabilă ajung la același public cu jumătate din bani, iar asta merită o oră de aritmetică întâi." },
      ],
    },
    {
      slug: "desktop", num: "03", eyebrow: "Desktop și embedded", kind: "Desktop",
      title: "Software pentru hala de lucru, și hardware-ul de sub el",
      intro:
        "Unele lucruri nu au ce căuta într-o filă de browser. O casă care trebuie să vândă și când pică internetul, o mașină care vorbește pe port serial, o placă ce încă nu există. Scriem software-ul acela, iar când nicio placă nu se potrivește, proiectăm placa.",
      includedHead: "Ce intră",
      included: [
        { h: "Aplicații desktop cross-platform", p: "Un singur cod pentru Windows, macOS și Linux, instalat și actualizat ca o aplicație adevărată, nu ca o pagină web care se preface." },
        { h: "Offline-first, se sincronizează la reconectare", p: "Aplicația își ține datele local și le împacă la revenirea rețelei. Un magazin nu se oprește din vândut fiindcă a pâlpâit legătura." },
        { h: "Serial, USB și aparate fiscale", p: "Case de marcat, scanere, cântare, imprimante. Am condus o casă fiscală TREMOL S dintr-o aplicație Electron, ceea ce înseamnă că munca de protocol e făcută, nu teoretică." },
        { h: "PCB propriu când nicio placă nu se potrivește", p: "Schemă și cablaj în KiCad, apoi firmware-ul și lanțul de viziune sau control care rulează pe el. O echipă pe toată stiva înseamnă că hardware-ul și software-ul sunt proiectate unul pentru celălalt." },
      ],
      buildsHead: "Ce construim",
      builds: [
        { h: "Casă de marcat și back office", p: "Vânzare, stoc, ture și bonuri fiscale pe aceeași mașină, care merge mai departe și când cade legătura în mijlocul unei tranzacții." },
        { h: "Terminale de depozit și de producție", p: "Scanare, cântărire, etichetare și mișcare de stoc, pe hardware care trebuie să reziste într-un depozit, nu pe un birou." },
        { h: "Comandă de mașini și dispozitive", p: "Serial, USB, Modbus și protocoale de producător, în spatele unui ecran pe care un operator îl folosește fără manual." },
        { h: "Dispozitive cu AI la margine", p: "Detecție, OCR sau clasificare care rulează pe placa însăși — fără drum până la un centru de date, fără cost pe apel, fără pană când cade linia." },
        { h: "Plăci proprii, de la schemă la producție", p: "Schemă și cablaj, prototip, firmware, apoi seria — pentru când nicio placă de raft nu are forma sau prețul potrivit." },
        { h: "IoT și rețele de senzori", p: "Microcontrolere care citesc senzori și raportează, cu partea de server și panoul care fac citirile să însemne ceva." },
      ],
      stackHead: "Stiva",
      stack: [
        { h: "Desktop", items: ["Electron", "React", "TypeScript", "Zustand", "TanStack Query", "MUI", "Express", "Sequelize", "PostgreSQL"], proof: "erp", also: ["Tauri", "Qt", "C#", ".NET", "WPF", "Python"] },
        { h: "Dispozitive și protocoale", items: ["serialport", "USB HID", "casă fiscală TREMOL S", "scanere de coduri", "cântare", "imprimante de bonuri"], proof: "erp", also: ["Modbus", "RS-485", "CAN", "MQTT"] },
        { h: "Embedded și edge", items: ["C++17", "RK3576", "RKNN", "GStreamer", "systemd", "LPDDR4X"], proof: "vision", also: ["ESP32", "STM32", "Raspberry Pi", "NVIDIA Jetson", "FreeRTOS", "Zephyr", "Yocto", "Buildroot"] },
        { h: "Proiectare hardware", items: ["KiCad", "schemă electrică", "cablaj PCB"], proof: "vision", also: ["serii de prototip", "asamblare", "carcasă"] },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Retail și HoReCa", p: "Casă de marcat, stoc, conformitate fiscală. Categoria în care o pană în program de lucru nu e un neajuns, ci venit pierdut." },
        { h: "Producție și logistică", p: "Orice citește un aparat, un senzor sau un cântar și trebuie să-l înregistreze corect din prima, fiindcă nimeni nu-l va reintroduce mai târziu." },
        { h: "Produse care au nevoie de hardware propriu", p: "Când o placă de raft are forma greșită, prețul greșit sau îi lipsește o interfață, a proiecta una e deseori mai ieftin decât a o ocoli la nesfârșit." },
      ],
    },
    {
      slug: "automation", num: "04", eyebrow: "Automatizări și integrări", kind: "Automation",
      title: "Sistemele pe care deja le plătești, făcute să vorbească între ele",
      intro:
        "Majoritatea firmelor n-au nevoie de încă o unealtă. Au nevoie ca cele patru pe care le au deja să nu mai fie insule, ca nimeni să nu mai retasteze o comandă dintr-un ecran în altul și să nu mai împace două tabele care trebuiau să fie unul.",
      includedHead: "Ce intră",
      included: [
        { h: "Integrare ERP, CRM și baze de date", p: "O singură sursă de adevăr, iar restul citesc din ea. De obicei asta înseamnă să alegi care sistem câștigă pe fiecare câmp — o decizie de business înainte să fie una tehnică." },
        { h: "API-uri externe și webhook-uri", p: "Procesatori de plăți, curieri, marketplace-uri, contabilitate. Inclusiv părțile nespectaculoase: reîncercări, idempotență, și ce se întâmplă când celălalt capăt e picat." },
        { h: "Notificări pe Telegram și email", p: "Evenimentul ajunge la om acolo unde el lucrează deja. Un dispecer care trăiește în Telegram n-ar trebui să deschidă un panou ca să afle că a venit o rezervare." },
        { h: "Sarcini programate și fluxuri de date", p: "Importuri, exporturi, împăcări și rapoarte care rulează singure și te anunță când cad — în loc să cadă tăcut până observă cineva că o cifră e greșită." },
      ],
      buildsHead: "Ce construim",
      builds: [
        { h: "Integrări între sisteme", p: "Magazin către ERP, ERP către contabilitate, CRM către depozit: o singură sursă de adevăr și restul citind din ea." },
        { h: "Boți care fac muncă adevărată", p: "Boți de Telegram, WhatsApp sau Viber care iau o comandă, răspund la o întrebare de status sau alertează un dispecer, legați la baza reală, nu la un scenariu de răspunsuri fixe." },
        { h: "Generare de documente și rapoarte", p: "Facturi, avize, contracte și rapoarte lunare produse din date, nu retastate din ele." },
        { h: "Sarcini programate și împăcări", p: "Importuri, exporturi și verificări de noapte care rulează singure și anunță pe cineva când cad, în loc să cadă tăcut o lună." },
        { h: "Colectare de date din surse fără API", p: "Prețuri, stocuri sau anunțuri citite din pagini când celălalt capăt nu oferă altă cale — pe un ritm și în limitele pe care sursa le impune." },
        { h: "Roboți pe interfețe vechi", p: "Când un sistem n-are niciun API, un script care îi conduce interfața așa cum ar face-o un om. Ultima soluție, și deseori singura." },
      ],
      stackHead: "Stiva",
      stack: [
        { h: "Rulare și programare", items: ["Node.js", "Express", "Python", "cron", "systemd timers", "workeri de fundal"], proof: "commerce", also: ["BullMQ", "Celery", "Redis", "RabbitMQ"] },
        { h: "Canale", items: ["Telegram Bot API", "Mailgun", "Resend", "SMTP"], proof: "elegance", also: ["WhatsApp Business API", "Viber", "gateway-uri SMS"] },
        { h: "Sisteme de business", items: ["baze de date făcute în casă", "API-uri REST și webhook", "schimb CSV și XML"], proof: "erp", also: ["1C", "Bitrix24", "amoCRM", "HubSpot", "Salesforce"] },
        { h: "Platforme low-code", items: ["n8n", "Make", "Zapier"] },
        { h: "Automatizare de browser", items: ["Playwright", "Puppeteer"], proof: "elegance", also: ["colectare programată", "roboți pe interfețe vechi"] },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Echipe care retastează aceleași date de două ori", p: "Cel mai clar semn că o integrare se plătește singură: în fișa postului cuiva apare expresia „copiază în”." },
        { h: "Firme care au depășit tabelele", p: "Tabelele sunt excelente până când le editează două persoane. Migrarea e de obicei mai puțină muncă decât anul de cârpeli dinaintea ei." },
        { h: "Oricine are unelte fără conector oficial", p: "Sisteme regionale, ERP-uri vechi, baze de date interne. Nu există integrare gata făcută, deci se scrie." },
      ],
    },
    {
      slug: "ai", num: "05", eyebrow: "AI care ajunge în producție", kind: "Automation",
      title: "Modele folosite unde își merită banii, cu limite în jurul lor",
      intro:
        "AI-ul e o componentă, nu un produs. Merită folosit acolo unde alternativa e un om care face ceva repetitiv, și merită evitat acolo unde o regulă deterministă e mai ieftină și niciodată greșită. Ingineria interesantă e chiar la granița dintre ele.",
      includedHead: "Ce intră",
      included: [
        { h: "Detecție și OCR pe dispozitiv", p: "Inferență care rulează pe mașină, nu într-un centru de date: fără drum dus-întors, fără cost pe apel, și merge mai departe când conexiunea nu merge. Am livrat detecție și OCR sub 100 ms pe o placă proprie." },
        { h: "Funcții cu LLM și verificare umană", p: "Modelul propune, un om aprobă, iar aprobarea face parte din flux, nu dintr-o politică pe care n-o respectă nimeni. Text generat pe care nu-l citește nimeni e mai rău decât niciun text." },
        { h: "Conținut generat, editat înainte de publicare", p: "Descrieri de produs schițate din fișa tehnică, apoi editate. Sute de articole devin fezabile fără ca magazinul să sune ca scris de o mașină, fiindcă tot un om l-a scris până la urmă." },
        { h: "Limite deterministe în jurul modelului", p: "Cod obișnuit ține granițele: cât are voie modelul să cheltuie, ce are voie să schimbe, și ce trebuie să confirme un om. Modelul nu e niciodată ultimul punct de control înaintea a ceva ireversibil." },
      ],
      buildsHead: "Ce construim",
      builds: [
        { h: "Detecție și recunoaștere pe dispozitiv", p: "Numărare, sortare sau citire de etichete și plăcuțe chiar pe mașină, la cadență video și fără factură de cloud." },
        { h: "OCR pe documente și bonuri", p: "Scanuri și fotografii transformate în câmpuri pe care un sistem le poate folosi, cu cele nesigure trimise la un om în loc să fie ghicite." },
        { h: "Text scris din propriile tale date", p: "Descrieri de produs, rezumate și răspunsuri generate din fișa tehnică sau din înregistrare, apoi editate de un om înainte să le vadă cineva din afară." },
        { h: "Clasificare și direcționare", p: "Tichete, mesaje și documente sortate automat în coada potrivită, cu cele nesigure trecute la un om." },
        { h: "Sprijin în cercetare și decizie", p: "Un model care citește aceleași date ca un om și propune o acțiune, cu cod obișnuit care decide ce are voie să facă cu ea." },
        { h: "Modele private, pe serverul tău", p: "Llama, Mistral sau similare rulând pe serverul tău, pentru când datele nu au voie să iasă din clădire sau prețul pe apel nu iese la socoteală." },
      ],
      stackHead: "Stiva",
      stack: [
        { h: "Viziune pe dispozitiv", items: ["YOLO", "ONNX Runtime", "RKNN", "OpenCV DNN", "PP-OCRv4", "C++17"], proof: "vision", also: ["NVIDIA Jetson", "TensorRT", "Tesseract"] },
        { h: "API-uri comerciale de model", items: ["OpenAI", "Anthropic Claude"], proof: "trading" },
        { h: "Modele pe server propriu", items: ["Llama", "Mistral", "Ollama"] },
        { h: "În jurul modelului", items: ["Python 3.12", "SQLAlchemy", "APScheduler", "motor de risc", "pas de verificare umană"], proof: "trading" },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Cataloage prea mari ca să fie scrise de mână", p: "Sute de produse fără descriere e un cost real. E și cazul în care schița plus editarea bat și scrisul de la zero, și publicarea output-ului brut." },
        { h: "Produse care trebuie să meargă fără rețea", p: "Săli de vânzare, vehicule, situri industriale. Dacă norul e opțional, inferența trebuie să se întâmple local, ceea ce schimbă ce model poți folosi și cum e construit." },
        { h: "Oricine nu e sigur dacă AI-ul are ce căuta aici", p: "Uneori răspunsul onest e că o regulă ar face-o mai bine și mai ieftin. Preferăm să spunem asta înainte să te angajezi, nu după." },
      ],
    },
    {
      slug: "data", num: "06", eyebrow: "Date și rapoarte", kind: "Automation",
      title: "Cifrele care spun dacă lucrul funcționează, într-un singur loc",
      intro:
        "Majoritatea firmelor nu duc lipsă de date. Duc lipsă de o singură versiune a lor. Cinci exporturi pe care nu le împacă nimeni nu sunt raportare, sunt o ceartă care așteaptă să se întâmple în fiecare lună.",
      includedHead: "Ce intră",
      included: [
        { h: "Model de date și schemă", p: "Partea care decide dacă fiecare întrebare de mai târziu e ușoară sau scumpă. Făcută o dată, cu grijă, înainte să se întărească în ceva ce nimeni nu mai îndrăznește să schimbe." },
        { h: "Panouri de raportare", p: "Cele câteva cifre pe care chiar se sprijină o decizie, actualizate automat. Nu un zid de grafice pe care toată lumea se oprește să-l deschidă după a doua săptămână." },
        { h: "Analitică fără invazie", p: "Măsurare care nu cere un banner de cookie-uri cât pagina. Primești cifrele de care ai nevoie, iar vizitatorii își păstrează intimitatea." },
        { h: "Migrare din tabele", p: "Mutarea istoricului fără să-l pierzi, inclusiv cazurile speciale ascunse în coduri de culoare și comentarii pe care niciun export nu le duce cu el." },
      ],
      buildsHead: "Ce construim",
      builds: [
        { h: "Un singur panou pentru firmă", p: "Venit, stoc, comenzi și marjă într-un singur loc, actualizate singure, în locul a cinci exporturi pe care nu le împacă nimeni." },
        { h: "Ecrane operaționale pentru echipă", p: "Ce ține deschis toată ziua un depozit, un dispecer sau un birou de suport, arătând ce are nevoie de atenție acum, nu luna trecută." },
        { h: "Rapoarte care se trimit singure", p: "Cifrele săptămânale sau lunare care ajung pe email sau în chat, în aceeași formă de fiecare dată, fără să le adune cineva." },
        { h: "Schemă și model de date", p: "Decizia care face fiecare întrebare de mai târziu ieftină sau scumpă, luată o dată și intenționat, nu din întâmplare." },
        { h: "Migrare din tabele", p: "Mutarea istoricului fără să pierzi înțelesul ascuns în coduri de culoare, comentarii și memoria unui singur om." },
        { h: "Analitică web fără invazie", p: "Cifre de trafic și conversie fără un banner de consimțământ cât pagina și fără să-ți predai vizitatorii unei rețele de publicitate." },
      ],
      stackHead: "Stiva",
      stack: [
        { h: "Panouri în propriul tău produs", items: ["React", "TypeScript", "Recharts", "TanStack Query", "agregare pe server"], proof: "erp", also: ["D3", "tabele virtualizate"] },
        { h: "BI gata făcut, pe serverul tău", items: ["Metabase", "Grafana", "Superset"] },
        { h: "Stocare și interogări", items: ["PostgreSQL", "MySQL", "SQLite", "indexare și optimizare de interogări"], proof: "erp", also: ["ClickHouse", "MongoDB", "Redis"] },
        { h: "Analitică", items: ["Umami", "evenimente pe server", "date din Search Console"], also: ["Plausible", "Matomo"] },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Firme care raportează din memorie", p: "Dacă cifra lunii depinde de cine a adunat-o, cifra e o părere. Să devină reproductibilă e de obicei o săptămână de muncă." },
        { h: "Echipe cu date în patru locuri", p: "Magazin, contabilitate, curier, tabel. Fiecare are dreptate despre partea lui și niciunul nu cade de acord la total." },
        { h: "Oricine nu știe ce să măsoare", p: "Se pornește de la decizie, nu de la grafic. Două cifre pe care le folosești bat patruzeci la care te uiți." },
      ],
    },
    {
      slug: "audit", num: "07", eyebrow: "Audit tehnic", kind: "Not sure yet",
      title: "O citire a ce ai, înainte să decizi dacă repari sau înlocuiești",
      intro:
        "Rescrierea e scumpă și deseori inutilă. Înainte să decizi, ajută să știi ce e de fapt în neregulă: dacă problema e codul, dacă e arhitectura, sau dacă o singură decizie proastă face tot răul. Primești constatările indiferent dacă ne angajezi după aceea sau nu.",
      includedHead: "Ce intră",
      included: [
        { h: "Performanță și Core Web Vitals", p: "Măsurate pe site-ul tău live, nu simulate. Cea mai mare desenare, deplasarea de layout, greutatea paginii, numărul de cereri — și care dintre ele se văd cu adevărat." },
        { h: "SEO tehnic și indexabilitate", p: "Dacă un crawler primește paginile tale, în general. Aici găsim cel mai des ceva serios: o aplicație JavaScript care servește un document gol fiecărui motor de căutare." },
        { h: "Arhitectură și cod", p: "Unde structura o să te încurce pe măsură ce produsul crește, și unde e în regulă. Inclusiv ce merită păstrat — de obicei mai mult decât se așteaptă lumea." },
        { h: "Verificare de dependențe și securitate", p: "Vulnerabilități cunoscute, pachete abandonate, chei în locuri greșite, și greșelile de configurare care ies la iveală doar sub sarcină sau sub atac." },
      ],
      buildsHead: "Ce ne uităm",
      builds: [
        { h: "Viteza pe telefoanele clienților tăi", p: "Core Web Vitals măsurate pe site-ul live și pe telefoane reale, nu pe un simulator cu fibră." },
        { h: "Dacă motoarele de căutare te văd", p: "Cea mai frecventă constatare serioasă: o aplicație JavaScript care servește un document gol fiecărui crawler — inclusiv celor din spatele răspunsurilor de AI." },
        { h: "Postura de securitate", p: "Verificări OWASP, dependențe vulnerabile și abandonate, chei în locuri greșite, și configurare de server care cedează doar sub atac." },
        { h: "Sarcină și punct de rupere", p: "Câți utilizatori ține până cade. Merită aflat înaintea unei campanii, nu în timpul ei." },
        { h: "Sănătatea bazei de date", p: "Interogări lente, indexuri lipsă, și tabelul care va opri site-ul la de patru ori traficul de azi." },
        { h: "Factura de cloud și monitorizarea", p: "Pentru ce plătești și dacă e folosit, și dacă te-ar anunța ceva în cazul în care s-ar strica la trei noaptea." },
        { h: "Arhitectură și cod", p: "Unde structura o să te încurce pe măsură ce produsul crește, și — de obicei mai mult decât se așteaptă lumea — ce merită păstrat." },
      ],
      stackHead: "Cu ce lucrăm",
      stack: [
        { h: "Performanță", items: ["Lighthouse", "Core Web Vitals", "WebPageTest", "analiză de bundle", "Chrome DevTools"], proof: "elegance" },
        { h: "Căutare și indexabilitate", items: ["Search Console", "Bing Webmaster Tools", "simulare de crawl", "validare JSON-LD", "verificare sitemap și hreflang"], proof: "actexpress" },
        { h: "Securitate", items: ["OWASP Top 10", "audit de dependențe", "scanare de chei", "TLS și antete"] },
        { h: "Teste de încărcare", items: ["k6", "Locust"] },
        { h: "Infrastructură și observabilitate", items: ["planuri de interogare și indexuri", "revizie nginx și PM2", "Sentry", "uptime și alertare", "revizie de cost cloud"], proof: "erp" },
      ],
      fitsHead: "Cui i se potrivește",
      fits: [
        { h: "Înainte de o decizie de rescriere", p: "Cea mai ieftină oră pe care o dai pe întrebarea asta. Uneori răspunsul e că rescrierea e corectă, și atunci ai dovezi, nu o senzație." },
        { h: "După ce ai moștenit un sistem", p: "Un dezvoltator nou, un CTO nou, o achiziție. Trebuie să știi ce ai acum înainte să promiți cuiva o dată." },
        { h: "Când traficul nu convertește", p: "Uneori marketingul e în regulă și site-ul e stricat în tăcere — lent pe telefoanele pe care le folosesc clienții tăi, sau invizibil pentru căutare." },
      ],
    },
  ],
  industriesPage: {
    eyebrow: "Soluții",
    title: "Fiecare domeniu și fiecare oraș pentru care am scris o pagină",
    intro: "Cincizeci și patru de situații, fiecare cu pagina ei: cum arată lucrul în domeniul acela, cât costă acolo și în ce serviciu se transformă până la urmă. Pornește de la cea care te descrie, nu de la o listă de tehnologii.",
    short: "Soluții",
    geo: "După loc",
    vertical: "După domeniu",
    geoService: "Un serviciu într-un loc",
    forService: "Situații scrise pe larg",
  },

  niche: {
    eyebrow: "Soluții",
    back: "Toate soluțiile",
    serviceHead: "Serviciul din spate",
    nearHead: "Situații apropiate",
    ctaHead: "Spune-ne ce ai și ce trebuie să facă. Răspundem cu ce e realist, nu cu o ofertă tip.",
  },

  blog: {
    eyebrow: "Blog",
    title: "Ce am învățat construind lucruri care trebuiau să funcționeze",
    intro: "Note de inginerie din proiecte aflate în producție, și răspunsuri la întrebările pe care le pun clienții înainte să se angajeze. Scrise când e ceva de scris, nu după un calendar.",
    listHead: "Toate articolele",
    minutes: "min de citit",
    published: "Publicat",
    updated: "Actualizat",
    toc: "În pagina asta",
    back: "← Toate articolele",
    serviceHead: "Serviciul de care ține",
    caseHead: "De unde vine",
    nextHead: "Următorul",
    empty: "Primele articole se scriu acum. Revino în curând, sau pune întrebarea direct — răspunsul ajunge mai repede așa.",
  },
  tool: {
    eyebrow: "Open source",
    title: "crawlview",
    tagline: "Vezi ce stochează efectiv motoarele de căutare și crawlerele AI pentru paginile tale — nu ce îți arată browserul.",
    intro: [
      "Browserul execută JavaScript, deci pagina din DevTools e o pagină terminată. Majoritatea crawlerelor nu îl execută. Ce păstrează ele poate fi un container gol, iar nimic de pe ecran nu ți-o spune.",
      "crawlview cere pagina o dată ca fiecare crawler, compară ce vine înapoi și arată unde browserul și mașinile nu sunt de acord. O comandă, fără cont, fără instalare.",
    ],
    runLbl: "Rulează",
    runHead: "O singură comandă",
    runNote: "Cere Node 20 sau mai nou. Comparația cu browserul folosește un Chrome deja existent pe mașină — nu se descarcă nimic pe ascuns.",
    outputLbl: "Cum arată",
    outputHead: "O pagină care se randează în browser și nicăieri altundeva",
    outputNote: "Tabelul e raportul. Propoziția de sub el e diagnosticul — partea pe care un crawl de HTML brut nu ți-o poate da.",
    checksLbl: "Ce verifică",
    checksHead: "Nouă grupe de verificări",
    checks: [
      { h: "Divergență", p: "Ce stochează fiecare crawler față de ce randează browserul: text, structured data și linkuri interne care apar doar după ce rulează JavaScript — așa devine un site întreg imposibil de descoperit chiar de pe prima lui pagină." },
      { h: "robots.txt, pentru fiecare crawler", p: "Dacă fiecare crawler are voie la exact acest URL și ce regulă a decis, inclusiv Google-Extended și Applebot-Extended: tokenuri care controlează răspunsurile AI dar nu cer niciodată nimic, deci nicio altă metodă nu le poate observa." },
      { h: "Directive de indexare", p: "Headerele X-Robots-Tag, invizibile în sursa paginii și cu prioritate peste eticheta meta. Un noindex rătăcit într-un header supraviețuiește luni de zile, pentru că nimănui nu-i trece prin cap să se uite acolo." },
      { h: "Structured data", p: "Proprietățile obligatorii, și afirmațiile pe care pagina însăși nu le face. Marcajul are voie să descrie doar ce vede un vizitator; un preț sau un telefon care există doar în JSON-LD e exact motivul pentru care se dau penalizări manuale." },
      { h: "Limba declarată față de limba reală", p: "O rută tradusă care randează textul implicit declară o limbă și servește alta. Semnul e că toate traducerile au același număr de cuvinte, iar site-ul pare impecabil până când cineva chiar citește." },
      { h: "hreflang", p: "Fiecare alternativă e cerută și verificată dacă face link înapoi. Un set în care un membru nu răspunde e aruncat în întregime — și celelalte traduceri își pierd legăturile." },
      { h: "Pregătire pentru răspunsuri AI", p: "Dacă crawlerele care nu execută JavaScript primesc ceva, cât text supraviețuiește extracției de conținut, dacă titlurile produc secțiuni utilizabile și dacă pagina poate fi citată." },
      { h: "Pe tot site-ul", p: "Adrese din sitemap care dau 404, redirectează, sunt noindex sau interzise; pagini care canonizează în altă parte; pagini orfane, nelinkuite de nicăieri; titluri duplicate; rute care sunt pe ascuns aceeași pagină." },
      { h: "Soft 404", p: "Un URL care nu poate exista răspunzând 200 — ce face o aplicație pe o singură pagină cu fiecare link scris greșit de pe internet care duce la tine." },
    ],
    ciLbl: "În pipeline",
    ciHead: "Pică build-ul, nu trimestrul",
    ci: [
      "O regresie de randare se descoperă de obicei peste săptămâni, în Search Console, după ce traficul deja a plecat. E o eroare de build pe care nimeni n-a pus-o să fie prinsă.",
      "crawlview iese cu cod diferit de zero când un crawler vede sub o proporție stabilită din textul browserului, iar un snapshot comis în repo transformă asta într-o comparație cu ultima rulare bună. O acțiune GitHub publicată o leagă direct de pull request.",
    ],
    linksLbl: "Unde stă",
    linksHead: "Sursă și pachet",
    repo: "Sursa pe GitHub",
    npm: "Pachetul pe npm",
    ctaHead: "L-ai rulat pe site-ul tău și vrei problemele rezolvate?",
    ctaBtn: "Spune-ne ce a găsit",
  },
  about: {
    eyebrow: "O echipă, patru ani, douăzeci de proiecte",
    title: "Despre",
    intro: [
      "coresmith.dev e un studio independent de inginerie software. Design, cod, publicare și partea de după, în care lucrul trebuie să continue să meargă — toate în casă, de aceeași echipă. Nu stă niciun manager de cont între tine și oamenii care construiesc software-ul, și nu se subcontractează nimic fără să-ți spunem.",
      "Echipa e mică intenționat, iar aria de lucru e îngustă intenționat. E o constrângere reală, nu o formulă de poziționare — luăm mai puține proiecte simultan, deci o dată de start e o limită adevărată, nu o poziție de negociere.",
      "Studioul e în Chișinău. Majoritatea proiectelor sunt pentru companii din Moldova și România, restul în alte patru țări.",
    ],
    approachLbl: "Cum funcționează",
    approachHead: "Întâi scopul, apoi prețul, apoi codul",
    approach: [
      "Majoritatea proiectelor o iau razna înainte să scrie cineva o linie, în golul dintre ce s-a cerut și ce era nevoie. De aceea prima discuție e despre ce trebuie să se schimbe în afacerea ta, nu despre ce framework folosim. Discuția aia e gratuită și de obicei durează un apel.",
      "Apoi primești un preț fix pentru un scop convenit, sau un abonament lunar dacă munca e cu adevărat deschisă. Fără listă de prețuri, fiindcă un preț dat înaintea scopului e o ghicire îmbrăcată în cifră.",
      "Lucrul se livrează cap-coadă: design, construcție, publicare, monitorizare. La predare codul e în repozitoriul tău, sub contul tău, cu documentație și runbook, iar ca să-ți ții software-ul în viață nu depinzi de noi.",
    ],
    valuesLbl: "Pe ce se sprijină",
    valuesHead: "Patru lucruri pe care nu le negociem",
    values: [
      { h: "Trebuie să ruleze în producție", p: "Trei din cele patru sisteme de pe site rulează astăzi în producție. Al patrulea e un experiment deschis și scrie asta pe pagina lui. Un demo care impresionează și nu ajunge nicăieri nu e muncă, e piesă de portofoliu." },
      { h: "Cifrele sunt măsurate", p: "Fiecare număr de pe site a fost măsurat, nu estimat: timpi de încărcare dintr-un browser real pe site-urile live, greutăți din rețea, numere de rute din sitemapuri. Ce nu putem măsura, nu afirmăm." },
      { h: "Ce ai plătit e al tău", p: "Cod în repozitoriul tău, documentație pe care o poți da altcuiva, runbook pentru părțile plictisitoare. Testul e simplu: ar putea alt inginer să preia asta fără să ne sune. Dacă nu, nu e terminat." },
      { h: "Răspunsul onest, devreme", p: "Dacă lucrul pe care l-ai cerut e lucrul greșit, afli înainte să te angajezi, nu peste trei luni. Uneori răspunsul onest e că nu-ți trebuie deloc un proiect." },
    ],
    worksLbl: "Cum lucrăm",
    worksHead: "Șase obiceiuri care decid rezultatul",
    works: [
      { h: "Înțelegem sarcina înainte să alegem unealta", p: "Aplicație web, aplicație desktop, integrare — astea sunt deja răspunsuri. Întrebarea la care răspund trebuie pusă prima, altfel iese o soluție bine construită la problema greșită." },
      { h: "Mai puține piese, care funcționează corect", p: "Fiecare dependență, fiecare serviciu și fiecare ecran e ceva ce se poate strica la trei dimineața. Cel mai mic sistem care face treaba e de obicei cel care mai stă în picioare peste doi ani." },
      { h: "Măsurăm în loc să ne certăm", p: "Dezacordurile despre performanță, structură sau prioritate se închid cu o măsurătoare. Durează o oră și termină discuția definitiv." },
      { h: "Demo săptămânal, pe care poți apăsa", p: "Nu rapoarte de stare. Ceva care rulează și pe care îl poți deschide și încerca, în fiecare săptămână, ca o direcție greșită să coste o săptămână, nu un trimestru." },
      { h: "Scop scris, în limbaj obișnuit", p: "Ce face, ce înseamnă terminat, și explicit ce nu e inclus. Ultima parte previne mai multe dispute decât primele două la un loc." },
      { h: "Livrarea include și ziua de după", p: "CI/CD, monitorizare și runbook fac parte din muncă, nu sunt un supliment. Un sistem pe care nimeni nu-l poate publica sau depana n-a fost livrat, a fost abandonat pe loc." },
    ],
    numbersLbl: "Unde stau lucrurile",
    numbersHead: "Cifrele, simplu",
    numbers: [
      { v: "4", l: "ani" },
      { v: "20", l: "proiecte" },
      { v: "6", l: "țări" },
    ],
    ctaHead: "Începe cu discuția despre scop. Nu costă nimic și de obicei e un singur apel.",
    ctaBtn: "Începe un proiect",
  },
  caseBack: "← Toate lucrările",
  caseVisit: "Deschide site-ul",
  caseCta: "Ai nevoie de ceva asemănător?",
  caseCtaBtn: "Începe un proiect",
  caseNext: "Cazul următor",
  caseCount: (i, total) => `Cazul ${String(i).padStart(2, "0")} / ${String(total).padStart(2, "0")}`,

  cases: [
    {
      scene: "erp",
      slug: "erp",
      index: 1,
      phases: { steps: ["desfăcut", "așezat", "asamblat", "sincronizat"], hint: "derulează ca să asamblezi sistemul" },
      title: "ERP de retail cu mai multe depozite + POS",
      meta: [{ b: "Operațiuni retail" }, { t: "2 luni" }, { t: "echipă proprie" }, { b: "livrat" }],
      shotAlt:
        "Randare a unei stații POS: un monitor cu bonul deschis — cablu HDMI, adaptor USB-C, SSD, mouse, hub și suport de monitor — cu subtotal, TVA, reducere și un total de 4 673 lei, plus butonul de încasare. Lângă el, terminalul de card și imprimanta fiscală, legate prin cabluri.",
      blocks: [
        {
          lbl: "Problema",
          h3: "Trei locații, un fișier Excel, nicio legătură cu fiscalul.",
          p: [
            "Stocul trăia în fișiere Excel copiate manual între locații. Casa de marcat nu știa ce e în depozit, iar nimic nu vorbea cu imprimanta fiscală, deci fiecare bon era retastat pe alt aparat.",
            "Erorile de numărătoare se adunau: o vânzare într-un magazin nu vedea o rezervare din altul.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "O schemă, un binar, offline din start.",
          p: [
            "O singură schemă PostgreSQL ca sursă de adevăr pentru stoc, prețuri și documente, cu clientul desktop vorbind cu ea printr-un strat subțire de Express. Clientul rulează în magazin și continuă să funcționeze când cade rețeaua.",
            "Imprimanta fiscală a fost partea grea: comandată pe serial, din exact aceeași acțiune care încheie vânzarea, ca un bon să nu poată devia niciodată de la tranzacția înregistrată.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "Client desktop pe o bază de date adevărată.",
          p: [
            "Înveliș Electron, React 19 cu TypeScript, Zustand pentru starea locală și TanStack Query pentru cache-ul de server. Express și Sequelize în spate, PostgreSQL cu 43 de tabele care acoperă locații, mișcări, documente și audit.",
          ],
        },
        { lbl: "Rezultatul", h3: "De la vânzare la bon fiscal, dintr-un clic.", p: [] },
      ],
      chips: ["Electron", "React 19", "TypeScript", "Zustand", "TanStack Query", "MUI", "Express", "Sequelize", "PostgreSQL", "serialport", "TREMOL S"],
      kpis: [{ v: "43", l: "tabele" }, { v: "3", l: "locații" }, { v: "2 luni", l: "până în producție" }],
      outcome:
        "Stocul e din nou un singur număr. Bonul fiscal se tipărește din aceeași tranzacție care înregistrează vânzarea, deci magazinul și evidența fiscală nu mai pot să se depărteze una de alta.",
    },
    {
      scene: "vision",
      slug: "vision",
      index: 2,
      phases: { steps: ["desfăcut", "așezat", "asamblat", "închis"], hint: "derulează ca să asamblezi placa" },
      title: "Produs de computer vision cu AI pe dispozitiv",
      meta: [{ b: "CV în timp real", t: " · hardware propriu" }, { t: "în desfășurare" }, { t: "echipă proprie" }, { b: "în producție" }],
      blocks: [
        {
          lbl: "Problema",
          h3: "Detecție și OCR sub 100 ms, pe o placă care încă nu exista.",
          p: [
            "Produsul avea nevoie de detecție de obiecte plus recunoaștere de text direct pe dispozitiv, suficient de rapid ca să pară instantaneu, pe hardware suficient de ieftin ca să poată fi produs în volum. Fără drum până în cloud.",
            "Și încă o constrângere: același binar C++ trebuia să ruleze și pe calculatorul de dezvoltare și pe placa embedded, altfel viteza de iterație s-ar fi prăbușit.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "Un singur pipeline, trei motoare de inferență.",
          p: [
            "Pipeline-ul de vision e scris o singură dată, peste o interfață de inferență cu trei implementări: ONNX Runtime pentru desktop, RKNN pentru NPU-ul de pe dispozitiv, OpenCV DNN ca rezervă. Schimbarea motorului e o modificare de configurație, nu o rescriere.",
            "Cadrele nu ies din memoria GPU între captură și inferență: GStreamer le mută zero-copy, și acolo s-a câștigat cea mai mare parte din bugetul de latență.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "Software-ul și placa pe care rulează.",
          p: [
            "C++17, YOLO pentru detecție, PP-OCRv4 (DBNet pentru găsirea textului, CRNN pentru citire). Dispozitivul se prezintă ca USB HID, deci calculatorul gazdă nu are nevoie de driver. systemd îl supraveghează.",
            "Placa e tot a noastră: un PCB pe 8 straturi în jurul unui RK3576 cu LPDDR4X, desenat în KiCad.",
          ],
        },
        { lbl: "Rezultatul", h3: "30 fps, pe dispozitiv, fără cloud.", p: [] },
      ],
      chips: ["C++17", "YOLO", "ONNX Runtime", "RKNN", "OpenCV DNN", "PP-OCRv4", "GStreamer", "USB HID", "systemd", "RK3576", "LPDDR4X", "KiCad"],
      kpis: [{ v: "30 fps", l: "pipeline" }, { v: "3", l: "motoare de inferență" }],
      outcome:
        "Detecția și OCR-ul rulează integral pe placă. Același binar se depanează pe un laptop, și de asta produsul a ajuns să fie livrat.",
    },
    {
      scene: null,
      site: { host: "neplaceshop.md", url: "https://neplaceshop.md", w: 1440, h: 4246 },
      slug: "commerce",
      index: 3,
      phases: { steps: [], hint: "" },
      title: "Magazin online bilingv de electronice",
      meta: [{ b: "Site", t: " · magazin online" }, { t: "2 limbi" }, { t: "echipă proprie" }, { b: "live" }],
      shotAlt:
        "Pagina principală neplaceshop.md în română: un banner portocaliu cu textul „Accesorii auto inteligente”, înconjurat de camere auto, suporturi de telefon și accesorii, deasupra barei de căutare.",
      blocks: [
        {
          lbl: "Problema",
          h3: "Două limbi, o singură bancă locală pentru plăți, sute de produse fără descrieri.",
          p: [
            "Catalogul trebuia să existe complet în română și în rusă, nu ca o traducere lăsată pe jumătate. Plățile trebuiau să treacă prin MAIB, banca locală, nu prin Stripe. Iar fiecare produs avea nevoie de o descriere pe care nimeni nu avea timp să o scrie.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "Limba ca dimensiune de bază, nu ca plugin.",
          p: [
            "Conținutul e stocat per limbă la nivel de câmp, deci un produs poate fi complet într-o limbă și în lucru în cealaltă, fără să strice pagina. Rutele conțin limba, deci ambele versiuni se indexează separat.",
            "Descrierile se generează o dată din fișa tehnică, apoi sunt corectate de un om înainte de publicare. Text generat pe care nu-l citește nimeni e mai rău decât lipsa textului.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "Magazinul, plățile și notificările din jurul lor.",
          p: [
            "Frontend React pe un API Express cu PostgreSQL. Gateway MAIB pentru plata cu cardul, facturare fiscală la finalizarea comenzii. Evenimentele de comandă pleacă spre Telegram pentru magazin și spre email pentru client.",
            "Paginile sunt pre-randate, deci crawlerul vede HTML complet, cu JSON-LD Schema.org pentru fiecare produs.",
          ],
        },
        { lbl: "Rezultatul", h3: "1 500 de vizitatori pe zi, ambele limbi complete.", p: [] },
      ],
      chips: ["React", "Express", "PostgreSQL", "MAIB gateway", "facturare fiscală", "OpenAI copy", "Telegram", "Mailgun", "React Snap", "JSON-LD"],
      kpis: [{ v: "1 500", l: "vizitatori pe zi" }, { v: "0,66 s", l: "cel mai mare desen" }, { v: "320", l: "adrese în sitemap" }],
      outcome:
        "Măsurat pe site-ul live: elementul cel mai mare se desenează în 0,66 s, iar o pagină de produs ajunge la crawler cu 700 de cuvinte proprii, nu cu o carcasă goală.",
    },
    {
      scene: "trading",
      slug: "trading",
      index: 4,
      phases: { steps: ["desfăcut", "așezat", "asamblat", "tranzacții"], hint: "derulează ca să asamblezi biroul" },
      title: "Bot de research pentru trading, condus de AI",
      meta: [{ b: "R&D", t: " · piețe de predicție + crypto" }, { t: "în desfășurare" }, { t: "echipă proprie" }, { b: "pe hârtie" }],
      blocks: [
        {
          lbl: "Întrebarea",
          h3: "Bate raționamentul indicatorii, pe poziții ținute mult?",
          p: [
            "Nu e un produs. E un experiment cu o întrebare care poate fi infirmată: pe poziții ținute zile, nu secunde, ia un model de limbaj care citește aceleași date ca un motor de reguli decizii mai bune decât motorul de reguli?",
            "Rulează pe hârtie, cu prețuri reale și o limită dură de pierdere zilnică.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "Modelul propune, motorul de risc decide mărimea.",
          p: [
            "Claude citește contextul de piață și produce o teză cu un număr de încredere. Motorul de risc, care e cod determinist obișnuit, transformă încrederea într-o mărime de poziție limitată de balanța disponibilă și de plafonul zilnic de pierdere. Modelul nu-și stabilește niciodată singur pariurile.",
            "Fiecare decizie e înregistrată împreună cu datele care au dus la ea, deci o tranzacție proastă poate fi reluată și discutată mai târziu.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "Planificator, bursă, registru, dashboard.",
          p: [
            "Python 3.12. ccxt pentru acces la burse, pandas-ta pentru indicatori, Anthropic Claude Opus pentru pasul de raționament, SQLAlchemy peste aiosqlite ca registru, APScheduler care învârte bucla, Streamlit pentru dashboard.",
          ],
        },
        { lbl: "Rezultatul", h3: "Promițător, și tot un experiment.", p: [] },
      ],
      chips: ["Python 3.12", "Claude Opus", "ccxt", "pandas-ta", "SQLAlchemy", "aiosqlite", "APScheduler", "Streamlit", "motor de risc"],
      kpis: [{ v: "~+1,5%", l: "P&L median zilnic" }, { v: "1 săpt.", l: "test pe hârtie" }],
      outcome:
        "O săptămână pe 100 de dolari pe hârtie e un semnal, nu un rezultat. E raportat aici ca ceea ce este: un experiment deschis, cu jurnalele intacte.",
    },
    {
      scene: null,
      site: { host: "createli.md", url: "https://createli.md", w: 1440, h: 5219 },
      slug: "createli",
      index: 5,
      phases: { steps: [], hint: "" },
      title: "Createli Group — mobilier la comandă",
      meta: [{ b: "Site" }, { t: "română" }, { t: "echipă proprie" }, { b: "live" }],
      shotAlt:
        "Pagina principală createli.md: o bucătărie la comandă fotografiată în lumină de zi, cu titlul „Mobilier personalizat pentru Toate Nevoile” și două butoane, unul pentru mobilă din lemn și unul pentru mobilier școlar.",
      blocks: [
        {
          lbl: "Problema",
          h3: "Un atelier, doi cumpărători care n-au nimic în comun.",
          p: [
            "Createli face mobilă din lemn la comandă pentru case și echipează școli și grădinițe. O familie care își alege bucătăria și un responsabil de achiziții care cumpără patruzeci de bănci nu caută aceeași pagină și nu citesc aceleași cuvinte. Un singur site trebuia să-i servească pe amândoi fără să devină un catalog care nu servește pe niciunul.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "Despărțirea se face pe primul ecran.",
          p: [
            "Hero-ul oferă cele două direcții ca două butoane, iar fiecare duce la o pagină scrisă în limba ei: materiale, finisaje și montaj pentru casă; rezistență, dimensiuni și livrare instituțională pentru școli.",
            "Produsele sunt date, nu pagini. Fiecare articol are ruta lui, generată din API, deci unul nou înseamnă un rând în bază, nu un deploy.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "React în față, randat în HTML static la build.",
          p: [
            "Vite și React, cu un API Express în spate. Fiecare rută de prezentare și fiecare pagină de produs e randată de un browser headless în timpul build-ului, deci un crawler sau un scraper de social primește HTML complet, cu meta care aparține paginii — nu o carcasă goală care se umple abia după ce rulează JavaScript.",
            "Prerandarea rulează contra unui server local, nu contra site-ului live. Așa build-ul rămâne închis în el însuși: niciun deploy nu depinde de un deploy anterior care să fie încă în picioare.",
          ],
        },
        { lbl: "Rezultatul", h3: "Treisprezece rute care ajung întregi.", p: [] },
      ],
      chips: ["React", "Vite", "TypeScript", "Tailwind", "shadcn/ui", "Express", "Puppeteer prerender", "react-helmet-async", "PM2", "nginx"],
      kpis: [{ v: "0,000", l: "deplasare de layout" }, { v: "2,3 s", l: "cel mai mare desen" }, { v: "13", l: "rute prerandate" }],
      outcome:
        "Măsurat pe site-ul live: nimic nu se mișcă în timp ce pagina se încarcă, iar elementul cel mai mare se desenează sub pragul „bun” al Google.",
    },
    {
      scene: null,
      site: { host: "elegance-transfer.com", url: "https://elegance-transfer.com", w: 1440, h: 7710 },
      slug: "elegance",
      index: 6,
      phases: { steps: [], hint: "" },
      title: "Elegance Transfer — rezervări în patru limbi",
      meta: [{ b: "Site", t: " · operator de transfer" }, { t: "4 limbi" }, { t: "echipă proprie" }, { b: "live" }],
      shotAlt:
        "Pagina principală elegance-transfer.com în rusă: un hero întunecat cu o berlină noaptea, titlul „Люкс и комфорт в каждой поездке” și un buton de rezervare.",
      blocks: [
        {
          lbl: "Problema",
          h3: "Șase coridoare vândute în patru piețe lingvistice.",
          p: [
            "Elegance conduce pe coridorul Moldova–Ucraina–România: curse spre aeroport, transferuri intercity, livrare de automobile. Clienții vin în rusă, română, engleză și ucraineană, iar cine caută un traseu anume nu e servit de o pagină principală care le enumeră pe toate.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "Fiecare traseu e pagina lui, în fiecare limbă.",
          p: [
            "Chișinău spre Odesa, București, Iași, Kiev, Liov și Cernăuți au fiecare o pagină, iar fiecare pagină există în toate cele patru limbi, cu adresa ei. O căutare pentru un singur coridor ajunge pe pagina acelui coridor.",
            "Formularul de rezervare scrie în bază și împinge cererea în Telegram, unde dispecerul deja lucrează. Nicio a doua unealtă de deschis, niciun dashboard de învățat.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "Patru limbi la nivel de câmp, Telegram pe post de consolă.",
          p: [
            "Vite și React, cu i18next care poartă cele patru limbi câmp cu câmp, deci o pagină poate fi completă într-una și în lucru în alta fără să se rupă. Express și MySQL în spate, plus un bot de Telegram care primește fiecare rezervare.",
            "După fiecare deploy, un browser headless randează toate cele 248 de rute în HTML static, deci fiecare pagină de traseu și fiecare mașină ajunge la crawler cu meta și JSON-LD proprii, nu cu o carcasă goală.",
          ],
        },
        { lbl: "Rezultatul", h3: "248 de rute, patru limbi, toate indexabile.", p: [] },
      ],
      chips: ["React", "Vite", "TypeScript", "Tailwind", "i18next", "Express", "MySQL", "Telegram Bot API", "Puppeteer prerender", "JSON-LD"],
      kpis: [{ v: "1,3 s", l: "cel mai mare desen" }, { v: "248", l: "rute prerandate" }, { v: "4", l: "limbi" }],
      outcome:
        "Măsurat pe site-ul live: 1,3 s până la cel mai mare desen și o deplasare de layout de 0,001, cu fiecare din cele patru limbi indexabilă separat.",
    },
    {
      scene: null,
      site: { host: "actexpress.md", url: "https://actexpress.md", w: 1440, h: 4841 },
      slug: "actexpress",
      index: 7,
      phases: { steps: [], hint: "" },
      title: "ActExpress — agenție de documente românești",
      meta: [{ b: "Site", t: " · agenție de documente" }, { t: "2 limbi" }, { t: "echipă proprie" }, { b: "live" }],
      shotAlt:
        "Pagina principală actexpress.md în română: un pașaport și acte fotografiate pe birou, titlul „Partenerul Tău de Încredere pentru Documente Românești” și un formular scurt de contact alături.",
      blocks: [
        {
          lbl: "Problema",
          h3: "Șapte proceduri, două limbi, un singur formular.",
          p: [
            "ActExpress se ocupă de cetățenie română, pașapoarte, buletine, permise de conducere, cazier judiciar, apostilă și traduceri autorizate. Șapte proceduri, fiecare cu actele ei, vândute unor clienți care citesc română sau rusă. Site-ul trebuia să le explice pe toate șapte suficient de clar încât omul să-și recunoască propriul caz, apoi să preia cererea cu detaliul care decide tot ce urmează: care procedură.",
          ],
        },
        {
          lbl: "Abordarea",
          h3: "Serviciul e un câmp, nu o frază.",
          p: [
            "Formularul poartă procedura ca pe un câmp separat, deci cererea ajunge deja sortată, nu ca text liber pe care cineva trebuie să-l citească și să-l clasifice înainte de a putea răspunde.",
            "Ambele limbi sunt în interfață de la prima încărcare, detectate din browser și comutabile manual. Politica de confidențialitate și termenii există în amândouă — o agenție care umblă cu actele altora și nu-și poate arăta propriii termeni se vinde greu.",
          ],
        },
        {
          lbl: "Arhitectura",
          h3: "Validat pe ambele părți, livrat în două locuri.",
          p: [
            "React pe Vite în față, Express și MySQL în spate. Formularul se validează în browser cu react-hook-form și încă o dată pe server cu express-validator, fiindcă o verificare care rulează în browser e o comoditate, nu o garanție.",
            "Fiecare cerere e scrisă în bază și trimisă pe email la birou în aceeași cerere, cu limitare de rată în fața endpointului, ca formularul să nu poată fi transformat în releu de mail.",
          ],
        },
        { lbl: "Rezultatul", h3: "Cel mai ușor dintre cele patru site-uri de aici.", p: [] },
      ],
      chips: ["React", "Vite", "react-i18next", "react-hook-form", "Tailwind", "Sass", "Express", "MySQL", "Nodemailer", "express-validator"],
      kpis: [{ v: "674 KB", l: "greutatea paginii" }, { v: "1,6 s", l: "cel mai mare desen" }, { v: "22", l: "cereri" }],
      outcome:
        "Măsurat pe site-ul live: 674 KB în 22 de cereri, sub jumătate din greutatea celorlalte trei, cu elementul cel mai mare desenat în 1,6 s.",
    },
  ],

  notFound: {
    eyebrow: "Eroare · 404",
    title: ["Ruta asta", "nu compilează."],
    text: (p) => `${p} nu se potrivește cu nicio rută înregistrată.`,
    notes: ["studioul într-o singură pagină", "șapte proiecte, și ce a schimbat fiecare", "ce construim, în șapte rânduri", "preț și durată, cu răspuns", "două întrebări și o descriere"],
  },

  footer: {
    rights: "© 2026 · Studio independent de inginerie",
    reach: "Chișinău, Moldova — lucrăm în Moldova și România",
    privacy: "Confidențialitate",
    terms: "Termeni",
  },

  legalOnlyEn: "Documentul acesta există doar în engleză. Versiunea engleză e cea care guvernează.",
};
